import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Download,
  Eye,
  Filter,
  Loader2,
  Package,
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api';

interface Order {
  id: number;
  service_name?: string;
  property_name?: string;
  postcode?: string;
  total_amount_pence: number;
  status: string;
  booking_status?: string;
  created_at: string;
}

const CompletedOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'COMPLETED' | 'CANCELLED'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await apiService.request('/customer/orders', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const closedOrders = response.data.filter((o: Order) => {
            const status = o.booking_status || o.status;
            return status === 'completed' || status === 'cancelled';
          });
          setOrders(closedOrders);
        }
      } catch (error) {
        console.error('Failed to fetch completed orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const filteredOrders = orders.filter((order) => {
    const status = (order.booking_status || order.status || '').toUpperCase();
    const haystack = `${order.id} ${order.service_name || ''} ${order.property_name || ''} ${order.postcode || ''}`.toLowerCase();
    const matchesSearch = haystack.includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'ALL' || status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalSpentPence = orders
    .filter((o) => (o.booking_status || o.status) === 'completed')
    .reduce((sum, o) => sum + (o.total_amount_pence || 0), 0);
  const propertiesServiced = new Set(orders.map((o) => o.property_name || o.postcode).filter(Boolean)).size;

  const stats = [
    { label: 'TOTAL ORDERS COMPLETED', value: String(orders.filter((o) => (o.booking_status || o.status) === 'completed').length) },
    { label: 'TOTAL SPENT', value: `£${(totalSpentPence / 100).toFixed(2)}` },
    { label: 'PROPERTIES SERVICED', value: String(propertiesServiced) },
  ];

  return (
    <DashboardLayout>
      <Helmet>
        <title>Completed Orders | Virewire</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Your completed and closed service orders
          </h1>
          <p className="text-gray-600 mt-2">
            Review your past service activity and order history.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-6">
          {stats.map((stat, idx) => (
            <Card key={idx} className="border-1">
              <CardContent className="p-6">
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                  {stat.label}
                </p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters & Search */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search orders, IDs or services..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value as 'ALL' | 'COMPLETED' | 'CANCELLED');
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none bg-white"
          >
            <option value="ALL">Status: All</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <div className="p-2 border border-gray-300 rounded-lg text-gray-400" title="Filtering is done via the dropdown and search box">
            <Filter className="h-5 w-5" />
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="border-1">
            <CardContent className="p-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-600">No completed or cancelled orders yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {paginatedOrders.map((order) => {
              const status = (order.booking_status || order.status || '').toUpperCase();
              const amountGBP = ((order.total_amount_pence ?? 0) / 100).toFixed(2);
              return (
                <Card
                  key={order.id}
                  className="border-1 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/order/${order.id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="text-xs font-bold text-gray-600">#ORD-{order.id}</p>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              status === 'COMPLETED'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {status}
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg mb-1">
                          {order.service_name || 'Service'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          📍 {order.property_name || order.postcode || 'Property'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          📅 {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>

                      <div className="text-right flex items-center gap-6">
                        <div>
                          <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                            Total Amount
                          </p>
                          <p className="text-2xl font-bold text-gray-900">
                            £{amountGBP}
                          </p>
                        </div>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => navigate(`/order/${order.id}`)}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            title="View order"
                          >
                            <Eye className="h-5 w-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => navigate(`/order/${order.id}#invoice-payment`)}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            title="View invoice"
                          >
                            <Download className="h-5 w-5 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && filteredOrders.length > 0 && (
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of{' '}
              {filteredOrders.length} orders
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded-lg font-semibold transition-colors ${
                    currentPage === page
                      ? 'bg-green-700 text-white'
                      : 'border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CompletedOrdersPage;
